import { useEffect, useState } from 'react'
import { sessaoAtual } from './api.js'
import { banco } from './banco.js'

/// Resolve o endereço de exibição de uma foto ou assinatura.
///
/// Não dá para pôr a URL da API direto num `<img src>`: a rota exige o token no
/// cabeçalho `Authorization`, e o navegador busca imagem sem cabeçalho nenhum. Com o
/// login de desenvolvimento isso passava despercebido, porque a rota aceitava
/// requisição sem credencial; com o Entra ID ligado, toda foto que não esteja mais
/// no aparelho vira imagem quebrada.
///
/// Ordem: blob local primeiro — é instantâneo e funciona sem rede. Só quando ele não
/// existe mais é que busca no servidor, com o token, e devolve um endereço de objeto.

const BASE = import.meta.env.VITE_API_BASE ?? '/api'

/// Endereços já resolvidos do servidor, por id de arquivo. O conteúdo é imutável (o
/// nome é o hash), então vale reaproveitar entre telas em vez de baixar de novo a
/// cada abertura de galeria.
const emCache = new Map<string, string>()

export type EstadoArquivo =
  | { estado: 'carregando'; url: null }
  | { estado: 'pronto'; url: string }
  | { estado: 'ausente'; url: null }

export function useArquivo(arquivoId: string | null | undefined): EstadoArquivo {
  const [resultado, setResultado] = useState<EstadoArquivo>(
    arquivoId ? { estado: 'carregando', url: null } : { estado: 'ausente', url: null },
  )

  useEffect(() => {
    if (!arquivoId) {
      setResultado({ estado: 'ausente', url: null })
      return
    }

    let cancelado = false
    /// Só o endereço criado por esta execução é revogado. Os que vêm do cache são
    /// compartilhados e revogá-los quebraria as outras telas que os exibem.
    let criadoAqui: string | null = null

    async function resolver() {
      const local = await banco.fotos.get(arquivoId!)
      if (cancelado) return
      if (local) {
        criadoAqui = URL.createObjectURL(local.blob)
        setResultado({ estado: 'pronto', url: criadoAqui })
        return
      }

      const guardado = emCache.get(arquivoId!)
      if (guardado) {
        setResultado({ estado: 'pronto', url: guardado })
        return
      }

      try {
        const sessao = await sessaoAtual()
        const resposta = await fetch(`${BASE}/arquivos/${arquivoId}`, {
          headers: sessao ? { Authorization: `Dispositivo ${sessao.token}` } : undefined,
        })
        if (!resposta.ok) throw new Error(String(resposta.status))

        const blob = await resposta.blob()
        if (cancelado) return

        const url = URL.createObjectURL(blob)
        emCache.set(arquivoId!, url)
        setResultado({ estado: 'pronto', url })
      } catch {
        if (!cancelado) setResultado({ estado: 'ausente', url: null })
      }
    }

    void resolver()
    return () => {
      cancelado = true
      if (criadoAqui) URL.revokeObjectURL(criadoAqui)
    }
  }, [arquivoId])

  return resultado
}

/// Esquece os endereços guardados. Chamado no logout — sem isto, a foto de uma obra
/// continuaria visível para o próximo usuário do mesmo aparelho enquanto a aba
/// estivesse aberta.
export function limparCacheDeArquivos(): void {
  for (const url of emCache.values()) URL.revokeObjectURL(url)
  emCache.clear()
}
